import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { safeStorage } from '../lib/safeStorage';

const GameTimerContext = createContext();
export const useGameTimer = () => useContext(GameTimerContext);

const STORAGE_KEY = 'axiom_game_timer';
const SHORT_COOLDOWN = 30 * 1000; // 30 seconds
const LONG_COOLDOWN = 60 * 60 * 1000; // 1 hour
const MAX_SESSIONS = 2; // 2 sessions then long break

// Session durations based on sobriety
const SESSION_DURATION_EARLY = 15 * 60 * 1000; // 15 minutes (< 24 weeks sober)
const SESSION_DURATION_VETERAN = 30 * 60 * 1000; // 30 minutes (24+ weeks sober)
const MAX_CONTINUOUS_PLAY = 30 * 60 * 1000; // 30 minutes max continuous

function getTimerState() {
  try {
    const raw = safeStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { sessionNumber: 1, sessionStart: null, cooldownEnd: null, totalSessionsUsed: 0 };
}

function saveTimerState(state) {
  safeStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function GameTimerProvider({ children }) {
  const [timerState, setTimerState] = useState(getTimerState);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef(null);

  // Get session duration based on user sobriety
  const getSessionDuration = useCallback(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const currentWeek = user.current_week || -2;
        if (currentWeek >= 24) return SESSION_DURATION_VETERAN;
      }
    } catch {}
    return SESSION_DURATION_EARLY;
  }, []);

  // SESSION_DURATION is now read freshly inside the per-second tick
  // (see useEffect below) so a user-week change takes effect immediately.

  const isLockedOut = useCallback(() => {
    if (!timerState.cooldownEnd) return false;
    return Date.now() < timerState.cooldownEnd;
  }, [timerState.cooldownEnd]);

  const isLongLockout = useCallback(() => {
    if (!timerState.cooldownEnd) return false;
    return timerState.totalSessionsUsed >= MAX_SESSIONS;
  }, [timerState.cooldownEnd, timerState.totalSessionsUsed]);

  const startSession = useCallback(() => {
    if (isLockedOut()) return;
    const newState = {
      ...timerState,
      sessionStart: Date.now(),
      cooldownEnd: null,
    };
    setTimerState(newState);
    saveTimerState(newState);
    setIsPlaying(true);
  }, [timerState, isLockedOut]);

  const endSession = useCallback(() => {
    const sessionsUsed = timerState.totalSessionsUsed + 1;
    const cooldownDuration = sessionsUsed >= MAX_SESSIONS ? LONG_COOLDOWN : SHORT_COOLDOWN;
    const newState = {
      sessionNumber: sessionsUsed >= MAX_SESSIONS ? 1 : timerState.sessionNumber + 1,
      sessionStart: null,
      cooldownEnd: Date.now() + cooldownDuration,
      totalSessionsUsed: sessionsUsed >= MAX_SESSIONS ? 0 : sessionsUsed,
    };
    setTimerState(newState);
    saveTimerState(newState);
    setIsPlaying(false);
  }, [timerState]);

  // Tick every second.  We re-read SESSION_DURATION from getSessionDuration
  // inside the tick so a user-week change (Recovery progress) takes effect
  // on the very next tick without remounting the provider.
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      const sessionDuration = getSessionDuration();
      const state = getTimerState();

      // Check cooldown
      if (state.cooldownEnd) {
        const remaining = state.cooldownEnd - Date.now();
        if (remaining <= 0) {
          const cleared = { ...state, cooldownEnd: null };
          setTimerState(cleared);
          saveTimerState(cleared);
          setCooldownRemaining(null);
        } else {
          setCooldownRemaining(remaining);
        }
        setTimeRemaining(null);
        return;
      }

      // Check active session
      if (state.sessionStart) {
        const elapsed = Date.now() - state.sessionStart;
        const remaining = sessionDuration - elapsed;
        if (remaining <= 0) {
          // Session expired — trigger cooldown
          const sessionsUsed = state.totalSessionsUsed + 1;
          const cooldownDuration = sessionsUsed >= MAX_SESSIONS ? LONG_COOLDOWN : SHORT_COOLDOWN;
          const newState = {
            sessionNumber: sessionsUsed >= MAX_SESSIONS ? 1 : state.sessionNumber + 1,
            sessionStart: null,
            cooldownEnd: Date.now() + cooldownDuration,
            totalSessionsUsed: sessionsUsed >= MAX_SESSIONS ? 0 : sessionsUsed,
          };
          setTimerState(newState);
          saveTimerState(newState);
          setIsPlaying(false);
          setTimeRemaining(null);
        } else {
          setTimeRemaining(remaining);
          setIsPlaying(true);
        }
      } else {
        setTimeRemaining(null);
        setIsPlaying(false);
      }
    }, 1000);

    return () => clearInterval(intervalRef.current);
  }, [getSessionDuration]);

  const formatTime = (ms) => {
    if (!ms || ms <= 0) return '0:00';
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const sessionLabel = `Session ${Math.min(timerState.totalSessionsUsed + 1, MAX_SESSIONS)} of ${MAX_SESSIONS}`;

  return (
    <GameTimerContext.Provider value={{
      isPlaying,
      isLockedOut: isLockedOut(),
      isLongLockout: isLongLockout(),
      timeRemaining,
      cooldownRemaining,
      formatTime,
      startSession,
      endSession,
      sessionLabel,
      sessionsUsed: timerState.totalSessionsUsed,
    }}>
      {children}
    </GameTimerContext.Provider>
  );
}
